export default {
  operateSettlement: {
    reportTitle: 'Business Reports',
    reportDescription: 'Review business income and prepaid balance data by date range and reporting dimension.',
    summaryTitle: 'Business Summary',
    metric: { share: 'Share' },
    prepaidSummary: 'Prepaid Balance Summary',
    prepaidPrincipalReceived: 'Principal Received',
    prepaidPrincipalRefund: 'Principal Refunded',
    prepaidGiftReceived: 'Gift Balance Received',
    prepaidGiftRefund: 'Gift Balance Refunded',
    dimensionSummary: 'Dimension Breakdown',
    filterDateRange: 'Date Range',
    filterStoreGroup: 'Store Region',
    filterProductKeyword: 'Product Name',
    groupAll: 'All Regions',
    groupEast: 'East China',
    groupSouth: 'South China',
    groupNorth: 'North China',
    dimensionStore: 'By Store',
    dimensionProduct: 'By Product',
    dimensionProductType: 'By Product Type',
    dimensionBackendCategory: 'By Back-office Category',
    dimensionFrontCategory: 'By Storefront Category',
    dimensionSalesChannel: 'By Sales Channel',
    dimensionPayChannel: 'By Payment Channel',
    metricActualNet: 'Actual Net Receipts',
    metricReceived: 'Amount Received',
    metricRefund: 'Refund Amount',
    metricPrepaidSpent: 'Prepaid Balance Spent',
    metricPrepaidRefund: 'Prepaid Balance Refund',
    metricPrepaidNet: 'Net Prepaid Spend',
    metricNetSales: 'Net Sales',
    metricShare: 'Share of Net Sales',
    metricPrepaidShare: 'Share of Net Prepaid Spend',
    help: {
      summaryActualNet:
        'Actual net receipts = actual receipts - actual refunds; the result may be negative. Prepaid spending and prepaid-balance refunds are excluded. Confirmed reversals are recorded on the reversal date and do not rewrite history.',
      summaryReceived:
        'Count actual receipts on the business date when the payment system confirms the entire order payment succeeded. Prepaid spending and unsuccessful orders are excluded; split payments across dates follow each successful payment date.',
      summaryRefund:
        'Actual funds refunded on the successful-refund business date, excluding prepaid-balance refunds. Unsuccessful refunds are excluded and historical results are not rewritten.',
      summaryPrepaidSpent:
        'Prepaid principal and gift balance used on the business date of a successfully completed order; this is not a top-up receipt.',
      summaryPrepaidRefund:
        'Prepaid principal and gift balance returned on the successful-refund business date; this is not an actual-funds outflow.',
      summaryPrepaidNet: 'Net prepaid spend = prepaid balance spent - prepaid balance refunded; the result may be negative.',
      dimension: {
        store: 'Grouped by the store attributed to the sale; displays the store name.',
        product: 'Grouped by sold product.',
        productType: 'Grouped by product type.',
        backendCategory: 'Grouped by the product’s back-office category.',
        frontCategory: 'Grouped by the product’s storefront category.',
        salesChannel: 'Grouped by order sales channel.',
        payChannel: 'Grouped by order payment channel.',
      },
      tableStoreGroup: 'For the store dimension, shows the store’s group. Other dimensions are not split by store group and show “-”.',
      tableOrderCount:
        'Sales orders with completed collection in the selected business-date range, counted by order ID. Multiple payments for one order count once; later refunds do not reduce the order count.',
      tableSalesQuantity: 'Product quantity sold, grouped by the selected filters and reporting dimension.',
      tableReceived:
        'Actual funds received for fully successful orders in the selected business-date range, excluding prepaid spending. Grouped by the current dimension, with refunds shown separately. Split payments are attributed by payment detail without counting the order total twice.',
      tableRefund:
        'Actual funds refunded on the successful-refund business date, excluding prepaid-balance refunds. Unsuccessful refunds are excluded and historical results are not rewritten.',
      tableActualNet:
        'Actual net receipts = actual receipts - actual refunds, matching the summary card. Prepaid spending and prepaid-balance refunds are excluded; the result may be negative.',
      tableNetSales: 'Net sales = actual receipts + prepaid spending - actual refunds - prepaid refunds.',
      tablePrepaidSpent:
        'Prepaid principal and gift balance used on the business date of a successfully completed order; this is not a top-up receipt.',
      tablePrepaidRefund: 'Prepaid principal and gift balance returned on the successful-refund business date; this is not an actual-funds outflow.',
      tablePrepaidNet: 'Net prepaid spend = prepaid balance spent - prepaid balance refunded; the result may be negative.',
      tableRefundOrderCount:
        'Count of original order IDs with a successful refund in the selected period. An order refunded across multiple days is counted once.',
      tableShare:
        'This row’s net sales ÷ total net sales in the filtered results × 100%. The denominator includes all filtered results, not only the current page; displays 0% when the denominator is 0.',
      tablePrepaidShare: 'This row’s net prepaid spend ÷ total net sales in the filtered results × 100%. Displays 0% when the denominator is 0.',
    },
    columnStore: 'Store Name',
    columnProduct: 'Product Name',
    columnProductType: 'Product Type',
    columnBackendCategory: 'Back-office Category',
    columnFrontCategory: 'Storefront Category',
    columnSalesChannel: 'Sales Channel',
    columnPayChannel: 'Payment Channel',
    columnStoreGroup: 'Store Region',
    columnOrderCount: 'Orders',
    columnSalesQuantity: 'Quantity Sold',
    columnRefundOrderCount: 'Refunded Orders',
    mockStoreA: 'East Store 1',
    mockStoreB: 'East Store 2',
    mockStoreC: 'East Store 3',
    mockStoreD: 'South Store 1',
    mockStoreE: 'South Store 2',
    mockStoreF: 'South Store 3',
    mockStoreG: 'North Store 1',
    mockStoreH: 'North Store 2',
    mockProductTicket: 'Single-Visit Ticket',
    mockProductPackage: 'Family Fun Package',
    mockProductGame: 'Interactive Game Experience',
    mockProductGift: 'Themed Souvenir',
    mockTypeTicket: 'Ticket',
    mockTypePackage: 'Package',
    mockTypeGame: 'Interactive Experience',
    mockTypeGoods: 'Retail Item',
    mockBackendTicket: 'Tickets',
    mockBackendGame: 'Attractions',
    mockBackendGoods: 'Retail',
    mockFrontPopular: 'Popular Picks',
    mockFrontFamily: 'Family Activities',
    mockFrontGift: 'Gifts and Souvenirs',
    mockChannelCounter: 'In-store POS',
    mockChannelMiniProgram: 'WeChat Mini Program',
    mockChannelSelfService: 'Self-service Kiosk',
    mockPayCash: 'Cash',
    mockPayWechat: 'WeChat Pay',
    mockPayAlipay: 'Alipay',
    detail: {
      title: 'Store Settlement Details',
      shopCode: 'Store Code',
      businessDate: 'Business Date',
      snapshot: 'Saved Settlement Snapshot',
      realtime: 'Live Basis',
      paymentStopped: 'Collection Stopped',
      paymentOpen: 'Collection Open',
      close: 'Close',
      retry: 'Retry',
      loading: 'Loading data…',
      mainFailed: 'Could not load settlement details. Please retry.',
      mainEmpty: 'No settlement details available.',
      empty: 'No details available.',
      scopeHint:
        'Scope: desktop registers and WeChat Mini Program. Count amounts and orders after full payment succeeds; exclude partial receipts and unsuccessful results.',
      intervalHint:
        'Business-day interval: {start} (inclusive) → {end} (exclusive). Statistics run up to {cutoff} (exclusive), which is not the settlement operation time.',
      queryTime: 'Query Time',
      attention: 'Items to Review',
      noAttention: 'None',
      snapshotHint: 'Pending refunds do not reduce net receipts. Refunds and reversals after settlement do not rewrite saved snapshots.',
      fundsTitle: 'Actual Funds Summary',
      actualPaymentAmount: 'Actual Funds Received',
      actualRefundAmount: 'Actual Funds Refunded',
      actualNetAmount: 'Actual Net Receipts',
      result: 'Settlement Result',
      orders: 'Order Overview',
      channels: 'Sales Channel Summary',
      payments: 'Payment Channel Summary',
      refunds: 'Refund Processing Overview',
      channelsHint:
        'All channel details are shown with a total. Each successfully paid order is counted once, not once per payment detail.',
      paymentsHint:
        'Actual funds have their own subtotal. Prepaid principal and gift balance share one subtotal and are excluded from actual funds; no cross-fund-category total is calculated.',
      prepaid: 'Prepaid Balance',
      principal: 'Principal',
      gift: 'Gift Balance',
      refundHint: 'As of {time}, all unfinished after-sales cases are included, regardless of processing stage.',
      settledRefundHint: 'Pending amount at settlement: {amount} · {time}',
      currentRefundHint:
        'Refund attention uses live data: {count} refunds / {amount} currently pending (queried at {time}), not the statistics cutoff values.',
      currentRefund: 'Currently Pending Refunds',
      cutoffRefund: 'Pending Refunds at Cutoff',
      settledRefund: 'Pending Refunds at Settlement',
      refundCount: 'Orders Pending Refund Processing',
      refundAmount: 'Pending Refund Amount',
      cashSummary: 'Cash Reconciliation Summary',
      cashSummaryFailed:
        'The main details failed to load, so cash totals are unknown. Device reconciliation loads independently and is not a store total.',
      cashSummaryLoading: 'Cash totals are loading. Device reconciliation loads independently.',
      cashSummaryUnknown: 'No cash summary is available; unknown amounts show “—”. Device reconciliation loads independently.',
      cashHint: 'Cash due for handover = opening cash + receipts + transfers in - refunds - transfers out - cash drops - other cash expenses.',
      cashScopeHint:
        'Opening cash: the first cash period uses the opening float; subsequent periods use the actual cash counted at the previous business-day cutoff. Cash flows are counted from the current business-day cutoff, not from count completion. Only register cash is reconciled; Mini Program activity does not participate in handover or counting. Device overages and shortages do not offset discrepancies; actual cash without a saved count shows “—”. See the table below for per-device discrepancies; no net discrepancy is shown in the store summary.',
      cashDifference: 'Cash Discrepancy',
      cashDifferenceHint: 'Overage {over} / Shortage {short}',
      devices: 'Cash Reconciliation',
      devicesFailed: 'Could not load device cash reconciliation. Please retry.',
      devicesEmpty: 'No device cash reconciliation records.',
      shifts: 'Shifts and Staff',
      shiftsFailed: 'Could not load shifts and staff. Please retry.',
      shiftsEmpty: 'No shift records.',
      history: 'Settlement Records',
      historyFailed: 'Could not load settlement history. Please retry.',
      historyEmpty: 'No settlement history.',
      events: 'Cash Flow Types',
      eventsFailed: 'Could not load cash events for this shift. Please retry.',
      eventsEmpty: 'No cash events for this shift.',
      eventsHint:
        'Cash events are queried by the current shift. Other cash expenses are summary values only; the API does not provide corresponding expense events.',
      expand: 'Expand Details',
      collapse: 'Collapse Details',
      details: 'Shift Details',
      shiftCash: 'Business-day Cash Breakdown',
      shiftTimeHint: 'Cash statistics: {start} (inclusive) → {end} (exclusive)',
      subtotal: 'Subtotal',
      total: 'Total',
      settlementStatus: 'Settlement Status',
      settlementNo: 'Settlement Number',
      settlementUserName: 'Settled By',
      createTime: 'Settlement Time',
      settlementMethod: 'Settlement Method',
      remark: 'Settlement Notes',
      dayStartTime: 'Business Day Start (inclusive)',
      dayEndTime: 'Business Day End (exclusive)',
      statsCutoffTime: 'Statistics Cutoff Time',
      settingVersionNo: 'Settings Version',
      id: 'Settlement Record ID',
      shopId: 'Store ID',
      createBy: 'Created By',
      revokeUserId: 'Revoked By ID',
      revokeUserName: 'Revoked By',
      revokeTime: 'Revocation Time',
      revokeReason: 'Revocation Reason',
      paymentOrderCount: 'Paid Orders',
      refundOrderCount: 'Refunded Orders',
      validOrderCount: 'Valid Orders',
      refundApplyOrderCount: 'Orders with Refund Applications',
      orderPaymentAmount: 'Order Payment Amount',
      orderRefundAmount: 'Order Refund Amount',
      orderNetAmount: 'Net Order Payment',
      channelType: 'Sales Channel',
      deviceName: 'Device Name',
      deviceIdentity: 'Device Name / Device Code',
      shiftIdentity: 'Shift Name / Shift Number',
      deviceCode: 'Device Code',
      fundCategory: 'Fund Category',
      payType: 'Payment Channel',
      paymentAmount: 'Receipts / Spending',
      paymentRefundAmount: 'Refund Amount',
      netAmount: 'Net Receipts / Net Spending',
      cashExpectedAmount: 'Cash Due for Handover',
      cashActualAmount: 'Counted Cash',
      cashOverAmount: 'Cash Overage',
      cashShortAmount: 'Cash Shortage',
      requiredDeviceCount: 'Devices to Count',
      checkedDeviceCount: 'Counted Devices',
      uncheckedDeviceCount: 'Uncounted Devices',
      cashDiffDeviceCount: 'Devices with Discrepancies',
      cashCheckStatus: 'Reconciliation Status',
      checkShiftNo: 'Shift',
      checkTime: 'Count Time',
      startTime: 'Statistics Start (inclusive)',
      endTime: 'Statistics End (exclusive)',
      openingCashAmount: 'Opening Cash',
      expectedCashAmount: 'Cash Due for Handover',
      actualCashAmount: 'Counted Cash',
      cashDiffAmount: 'Cash Discrepancy',
      cashCollectionAmount: 'Cash Receipts',
      cashRefundAmount: 'Cash Refunds',
      cashInAmount: 'Cash Transfers In',
      cashOutAmount: 'Cash Transfers Out',
      cashDropAmount: 'Cash Drops',
      otherCashExpenseAmount: 'Other Cash Expenses',
      openingSource: 'Opening Source (raw value)',
      openingSourceId: 'Opening Source ID',
      shiftNo: 'Shift Number',
      shiftName: 'Shift Name',
      shiftList: 'Shift List',
      viewMore: 'View More',
      participants: 'Participating Staff',
      handoverNo: 'Handover Number',
      closeUserName: 'Handover Staff',
      closeTime: 'Handover Time',
      handoverStatus: 'Handover Status',
      sliceStartTime: 'Shift Slice Start (inclusive)',
      sliceEndTime: 'Shift Slice End (exclusive)',
      orderCount: 'Shift Order Count',
      shiftCount: 'Shift Count',
      eventTime: 'Event Time',
      eventType: 'Cash Flow Type',
      amount: 'Event Amount',
      operatorName: 'Operator',
      reason: 'Reason',
      orderNo: 'Order Number',
      businessNo: 'Business Number',
      enums: {
        status: { PENDING: 'Pending', SETTLED: 'Settled', REVOKED: 'Revoked' },
        method: { MANUAL: 'Manual Settlement', AUTOMATIC: 'Automatic Settlement' },
        channel: { 3: 'Desktop Register', 4: 'WeChat Mini Program' },
        fund: { ACTUAL_FUNDS: 'Actual Funds', PREPAID_PRINCIPAL: 'Prepaid Principal', PREPAID_GIFT: 'Prepaid Gift Balance' },
        pay: {
          WECHAT: 'WeChat Pay',
          ALIPAY: 'Alipay',
          QQPAY: 'QQ Wallet',
          UNINPAY: 'UNINPAY',
          BESTPAY: 'BestPay',
          JDPAY: 'JD Pay',
          DIGICCY: 'Digital CNY',
          CASH: 'Cash',
        },
        cash: {
          UNCHECKED: 'Some registers not counted',
          BALANCED: 'No discrepancy',
          DIFFERENCE: 'Cash discrepancy',
          DIFFERENCE_UNCHECKED: 'Cash discrepancy; some registers not counted',
        },
        attention: { CASH_DIFFERENCE: 'Cash reconciliation discrepancy', PENDING_REFUND: 'Refunds pending' },
        handover: { OPEN: 'Open', CLOSED: 'Closed' },
        event: {
          COLLECTION: 'Receipt',
          REFUND: 'Refund',
          CASH_IN: 'Transfer In',
          CASH_OUT: 'Transfer Out',
          CASH_DROP: 'Cash Drop',
          OPEN_DRAWER: 'Open Drawer',
        },
      },
    },
    board: {
      loading: 'Loading daily settlement data',
      loadFailed: 'Could not load daily settlement data. Please retry.',
      retry: 'Retry',
      refresh: 'Refresh',
      shopCode: 'Store Code',
      shopFilter: 'Stores',
      filterLimit: 'Store name and store code each allow up to 100 characters. Select no more than 100 stores.',
      dataTime: 'Query Time',
      statsCutoffTime: 'Statistics Cutoff Time',
      actualNetAmount: 'Actual Net Receipts',
      settlementNo: 'Settlement Number',
      settlementMethod: 'Settlement Method',
      settlementUserName: 'Settled By',
      paymentStatus: 'Collection Status',
      paymentStopped: 'Collection Stopped',
      paymentOpen: 'Collection Open',
      realtime: 'Live Data',
      detailBoundary:
        'The home page provides store settlement summaries only, not order, channel, payment, or shift details, nor store cash and refund amount breakdowns.',
      method: { MANUAL: 'Manual Settlement', AUTOMATIC: 'Automatic Settlement' },
      cashStatus: {
        UNCHECKED: 'Some registers not counted',
        BALANCED: 'Balanced',
        DIFFERENCE: 'Discrepancy',
        DIFFERENCE_UNCHECKED: 'Cash discrepancy; some registers not counted',
      },
      attention: { CASH_DIFFERENCE: 'Cash reconciliation discrepancy', PENDING_REFUND: 'Refunds pending' },
      status: { PENDING: 'Pending', SETTLED: 'Settled' },
      emptyStores: 'No stores match the current filters. Adjust your filters.',
      cutoffHint:
        'Query time is the time of this query. The statistics cutoff is exclusive and is not the settlement operation time. The business date is shown unchanged.',
    },
    businessDate: 'Business Date',
    conclusionTitle: 'Daily Settlement Overview',
    conclusionSummary: '{settled} of {total} stores have settled; {pending} are pending.',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    metrics: 'Settlement Metrics',
    settlementProgress: 'Settlement Progress',
    settlementProgressHint: '{percentage}% complete, {pending} stores remaining',
    snapshotNetIncome: 'Settled Net Income',
    snapshotNetIncomeHint: 'Settlement snapshots completed for {count} stores',
    pendingEstimatedIncome: 'Estimated Income Pending Settlement',
    pendingEstimatedIncomeHint: 'From {count} stores awaiting settlement',
    attentionStores: 'Stores Requiring Attention',
    storesUnit: 'stores',
    attentionStoresHint: 'Cash discrepancies or pending refunds',
    pendingRefundAmount: 'Pending Refund Amount',
    pendingRefundAmountHint: '{count} refunds pending',
    attentionTitle: 'Stores Requiring Attention',
    attentionSubtitle: 'These stores have settlement or reconciliation items to review',
    noAttentionStores: 'No stores require attention',
    storeOverview: 'Store Settlement List',
    storeOverviewSubtitle: '{total} stores match the current filters',
    statusFilter: 'Settlement Status Filter',
    notSettled: 'Not settled',
    noAttention: 'None',
    viewDetails: 'View Details',
    storeSettlementDetails: 'Store Settlement Details',
    readOnlySnapshot: 'Read-only Snapshot',
    snapshotAndEstimate: 'Settlement Snapshot and Live Estimate',
    snapshotAt: 'Snapshot time: {time}',
    notAvailable: 'Not available',
    realtimeEstimate: 'Live Estimated Income',
    snapshotEstimateNote: 'The settlement snapshot is saved when settlement is completed; the live estimate changes with orders.',
    amountSummary: 'Amount Summary',
    grossIncome: 'Gross Income',
    discountAmount: 'Discounts',
    refundAmount: 'Refund Amount',
    netIncome: 'Net Income',
    orderOverview: 'Order Overview',
    totalOrders: 'Total Orders',
    completedOrders: 'Completed Orders',
    refundedOrders: 'Refunded Orders',
    cancelledOrders: 'Cancelled Orders',
    channelSummary: 'Channel Summary',
    channelDineIn: 'Dine-in',
    channelTakeaway: 'Pickup',
    channelDelivery: 'Delivery',
    paymentWechat: 'WeChat Pay',
    paymentAlipay: 'Alipay',
    paymentCash: 'Cash',
    paymentOther: 'Other Payments',
    refundAndCashCheck: 'Refund and Cash Reconciliation',
    refundOverview: 'Refund Overview',
    pendingRefunds: 'Pending Refund Amount',
    refundCount: 'Refund Count',
    refundStatus: 'Refund Status',
    refundStatusValue: { pending: 'Pending', none: 'No pending refunds' },
    cashReconciliation: 'Cash Reconciliation',
    expectedCash: 'Expected Cash',
    actualCash: 'Actual Cash',
    cashDifference: 'Cash Difference',
    reconciliationResult: 'Reconciliation Result',
    reconciliation: { matched: 'Matched', difference: 'Discrepancy' },
    shiftInformation: 'Shift Information',
    shiftCount: '{count} shifts',
    shiftMorning: 'Morning Shift',
    shiftEvening: 'Evening Shift',
    mockCashierA: 'Alex Chen',
    mockCashierB: 'Jamie Li',
    shiftCashier: 'Cashier: {name}',
    shiftNetIncome: 'Shift Net Income',
    searchKeyword: 'Store Name',
    storeScope: 'Store Region',
    scopeAll: 'All Regions',
    scopeNorth: 'North China',
    scopeEast: 'East China',
    scopeSouth: 'South China',
    scopeWest: 'West China',
    settlementStatus: 'Settlement Status',
    status: { all: 'All', settled: 'Settled', pending: 'Pending', attention: 'Needs attention' },
    table: {
      storeName: 'Store Name',
      scope: 'Region',
      status: 'Settlement Status',
      netAmount: 'Net Income',
      refundAmount: 'Refund Amount',
      settlementTime: 'Settlement Time',
      attention: 'Items to Review',
      action: 'Actions',
    },
    currencySymbol: '¥',
    attentionCashDifference: 'Cash reconciliation discrepancy',
    attentionRefundPending: 'Refunds pending',
    attentionNotSettled: 'Settlement not completed',
    attentionLateSettlement: 'Settlement completed late',
    mockStore01: 'Store 01',
    mockStore02: 'Store 02',
    mockStore03: 'Store 03',
    mockStore04: 'Store 04',
    mockStore05: 'Store 05',
    mockStore06: 'Store 06',
    mockStore07: 'Store 07',
    mockStore08: 'Store 08',
    mockStore09: 'Store 09',
    mockStore10: 'Store 10',
    mockStore11: 'Store 11',
    mockStore12: 'Store 12',
    mockStore13: 'Store 13',
    mockStore14: 'Store 14',
    mockStore15: 'Store 15',
    mockStore16: 'Store 16',
    mockStore17: 'Store 17',
    mockStore18: 'Store 18',
    mockStore19: 'Store 19',
    mockStore20: 'Store 20',
  },
};
